import React from "react";
import { Check } from "lucide-react";
import Switch from "@mui/material/Switch";
import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";

const ColumnSelector = ({
  columns,
  selectedColumns,
  onToggleColumn,
  onSelectAll,
  columnDetails,
  onCdeChange,
  onDescriptionChange,
  onMetadataChange,
}) => {
  const allSelected = selectedColumns.length === columns.length && columns.length > 0;
  const isIndeterminate = selectedColumns.length > 0 && selectedColumns.length < columns.length;

  return (
    <TableContainer component={Paper} sx={{ 
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      overflow: 'hidden'
    }}>
      <Table size="small" sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f8fafc' }}>
            <TableCell padding="checkbox" sx={{ 
              borderBottom: '2px solid #e5e7eb',
              backgroundColor: '#f8fafc'
            }}>
              <Checkbox
                indeterminate={isIndeterminate}
                checked={allSelected}
                onChange={onSelectAll}
                inputProps={{ 'aria-label': 'select all columns' }}
                sx={{
                  color: '#ff32a0',
                  '&.Mui-checked': {
                    color: '#ff32a0',
                  },
                  '&.MuiCheckbox-indeterminate': {
                    color: '#ff32a0',
                  }
                }}
              />
            </TableCell>
            <TableCell sx={{
              fontWeight: "600", 
              fontSize: '14px', 
              color: "#ff32a0",
              borderBottom: '2px solid #e5e7eb',
              backgroundColor: '#f8fafc',
              padding: '16px 8px'
            }}>
              CDE
            </TableCell>
            <TableCell sx={{
              fontWeight: "600", 
              fontSize: '14px', 
              color: "#ff32a0",
              borderBottom: '2px solid #e5e7eb',
              backgroundColor: '#f8fafc',
              padding: '16px 8px'
            }}>
              Select Column
            </TableCell>
            <TableCell sx={{
              fontWeight: "600", 
              fontSize: '14px', 
              color: "#ff32a0",
              borderBottom: '2px solid #e5e7eb',
              backgroundColor: '#f8fafc',
              padding: '16px 8px'
            }}>
              Metadata
            </TableCell>
            <TableCell sx={{
              fontWeight: "600", 
              fontSize: '14px', 
              color: "#ff32a0",
              borderBottom: '2px solid #e5e7eb',
              backgroundColor: '#f8fafc',
              padding: '16px 8px'
            }}>
              Business Description
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {columns.map((column, index) => {
            const isSelected = selectedColumns.includes(column.id);
            return (
              <TableRow 
                key={column.id} 
                hover 
                selected={isSelected}
                sx={{
                  backgroundColor: isSelected ? '#f0f8ff' : 'white',
                  '&:hover': {
                    backgroundColor: isSelected ? '#e6f3ff' : '#f9fafb'
                  },
                  '&:nth-of-type(even)': {
                    backgroundColor: isSelected ? '#f0f8ff' : '#fafbfc'
                  },
                  transition: 'background-color 0.2s ease'
                }}
              >
                <TableCell padding="checkbox" sx={{ 
                  borderBottom: '1px solid #f3f4f6',
                  padding: '12px 8px'
                }}>
                  <Checkbox
                    checked={isSelected}
                    onChange={() => onToggleColumn(column.id)}
                    inputProps={{ 'aria-label': `select column ${column.name}` }}
                    sx={{
                      color: '#0000a0',
                      '&.Mui-checked': {
                        color: '#0000a0',
                      }
                    }}
                  />
                </TableCell>
                <TableCell sx={{ 
                  borderBottom: '1px solid #f3f4f6',
                  padding: '12px 8px'
                }}>
                  <Switch
                    checked={columnDetails[column.id]?.cde || false}
                    onChange={(e) => onCdeChange(column.id, e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#ff9800',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 152, 0, 0.08)',
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#ff9800',
                      },
                    }}
                    disabled={!isSelected}
                  />
                </TableCell>
                <TableCell sx={{ 
                  borderBottom: '1px solid #f3f4f6',
                  padding: '12px 8px'
                }}>
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900 text-sm">{column.name}</span>
                    <span className="text-xs text-gray-500 mt-1">({column.type})</span>
                  </div>
                </TableCell>
                <TableCell sx={{ 
                  borderBottom: '1px solid #f3f4f6',
                  padding: '12px 8px'
                }}>             
                    <span className="font-medium text-gray-900 text-sm">{column.metadata}</span>
                </TableCell>
                <TableCell sx={{ 
                  borderBottom: '1px solid #f3f4f6',
                  padding: '12px 8px'
                }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter business description..."
                    value={columnDetails[column.id]?.description || ""}
                    onChange={(e) => onDescriptionChange(column.id, e.target.value)}
                    disabled={!isSelected}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        '&.Mui-focused fieldset': {
                          borderColor: '#0000a0',
                          borderWidth: '2px'
                        },
                        '&:hover fieldset': {
                          borderColor: '#0000a0'
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#f9fafb'
                        }
                      }
                    }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ColumnSelector;
